import { useCallback, useEffect, useRef, useState } from 'react';
import { Archive, Loader2, Upload } from 'lucide-react';
import client, { apiError } from './adminApi';
import { Pagination } from './components/ui';
import { useAdminAuth } from './AdminAuth';

// Media library — validated uploads to object storage, metadata editing,
// usage-checked archiving. No large binaries in MongoDB.
export default function MediaPage() {
  const { user } = useAdminAuth();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [alt, setAlt] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [archiveInfo, setArchiveInfo] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await client.get('/admin/media', { params: { q: q || undefined, page } });
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Media could not be loaded.'));
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const form = new FormData();
      form.append('file', file);
      await client.post(`/admin/media?alt=${encodeURIComponent(alt)}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNotice(`Uploaded ${file.name}.`);
      setAlt('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      setError(apiError(err, 'Upload failed.'));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (mediaId, force = false) => {
    setError('');
    try {
      const res = await client.post(`/admin/media/${mediaId}/archive`, null, { params: { force } });
      if (res.data.status === 'in_use') {
        setArchiveInfo({ mediaId, usedBy: res.data.usedBy });
        return;
      }
      setNotice('Archived.');
      setArchiveInfo(null);
      await load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const saveAlt = async (mediaId, value, decorative) => {
    try {
      await client.patch(`/admin/media/${mediaId}`, { alt: value, decorative });
      setNotice('Saved.');
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div data-testid="admin-media">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Media Library</h1>
      <p className="mt-1 text-sm text-slate-500">Images and documents. JPEG, PNG, WebP, GIF or PDF up to 10 MB.</p>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-52 flex-1">
            <label htmlFor="media-alt" className="text-xs font-bold text-navy-900">Alt text (required for images)</label>
            <input id="media-alt" value={alt} onChange={(e) => setAlt(e.target.value)} data-testid="media-alt" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" placeholder="Describe the image for screen readers" />
          </div>
          <div>
            <input ref={fileRef} id="media-file" type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" onChange={(e) => upload(e.target.files?.[0])} className="sr-only" data-testid="media-file" />
            <label htmlFor="media-file" className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-navy-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? 'Uploading…' : 'Upload File'}
            </label>
          </div>
          <div className="relative">
            <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search files…" aria-label="Search media" className="h-10 w-52 rounded-md border border-slate-300 px-3 text-sm" />
          </div>
        </div>
        {notice && <p role="status" className="mt-3 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800" data-testid="media-notice">{notice}</p>}
        {error && <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="media-error">{error}</p>}
      </div>

      {!data ? (
        <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
      ) : data.items.length === 0 ? (
        <p className="mt-6 rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 text-center text-sm font-semibold text-slate-500" data-testid="media-empty">No media yet. Upload your first file.</p>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="media-grid">
          {data.items.map((m) => (
            <figure key={m.mediaId} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`media-${m.mediaId}`}>
              {m.contentType.startsWith('image/') ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt={m.alt || m.filename} className="h-36 w-full object-cover" loading="lazy" />
              ) : (
                <div className="flex h-36 items-center justify-center bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-400">PDF document</div>
              )}
              <figcaption className="p-3">
                <p className="truncate text-xs font-bold text-navy-900" title={m.filename}>{m.filename}</p>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {(m.size / 1024).toFixed(0)} KB{m.width ? ` · ${m.width}×${m.height}` : ''} · by {m.uploadedBy}
                </p>
                <input
                  aria-label={`Alt text for ${m.filename}`}
                  defaultValue={m.alt}
                  onBlur={(e) => e.target.value !== m.alt && saveAlt(m.mediaId, e.target.value, m.decorative)}
                  className="mt-2 w-full rounded border border-slate-200 px-2 py-1 text-xs"
                  placeholder="Alt text"
                />
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(m.url)}
                    className="text-[11px] font-bold text-navy-900 underline-offset-2 hover:underline"
                  >
                    Copy URL
                  </button>
                  {['owner', 'administrator'].includes(user.role) && (
                    <button type="button" onClick={() => archive(m.mediaId)} data-testid={`media-archive-${m.mediaId}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 hover:underline">
                      <Archive className="h-3 w-3" /> Archive
                    </button>
                  )}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {data && <Pagination page={page} perPage={24} total={data.total} onPage={setPage} />}

      {archiveInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/60 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl bg-white p-6" data-testid="media-usage-warning">
            <h2 className="text-lg font-extrabold text-navy-900">This file is in use</h2>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-600">
              {archiveInfo.usedBy.map((u, i) => <li key={i}>{u.type}: {u.label}</li>)}
            </ul>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setArchiveInfo(null)} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-navy-900">Keep File</button>
              <button type="button" onClick={() => archive(archiveInfo.mediaId, true)} data-testid="media-force-archive" className="rounded-md bg-red-700 px-4 py-2 text-xs font-bold text-white">Archive Anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
